import React, { useState, useEffect } from 'react';
import { testimonialsAPI, uploadAPI } from '../../lib/api';
import { Plus, Pencil, Trash, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const emptyTestimonial = () => ({
  customer_name: '',
  location: '',
  testimonial: '',
  image_url: '',
  image_file_id: null,
  active: true,
  display_order: 0
});

const TestimonialsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTestimonial());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await testimonialsAPI.adminGetTestimonials();
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyTestimonial());
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...emptyTestimonial(), ...t });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyTestimonial());
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.uploadFile(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.file_id}`;
      setForm(prev => ({ ...prev, image_url: url, image_file_id: res.data.file_id }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.testimonial.trim()) {
      toast.error('Name and testimonial text are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        location: form.location.trim(),
        testimonial: form.testimonial.trim(),
        image_url: form.image_url || '',
        image_file_id: form.image_file_id || null,
        active: !!form.active,
        display_order: parseInt(form.display_order || 0)
      };
      if (editing) {
        await testimonialsAPI.updateTestimonial(editing.testimonial_id, payload);
        toast.success('Testimonial updated');
      } else {
        await testimonialsAPI.createTestimonial(payload);
        toast.success('Testimonial added');
      }
      closeForm();
      fetchItems();
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete testimonial from "${t.customer_name}"?`)) return;
    try {
      await testimonialsAPI.deleteTestimonial(t.testimonial_id);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="testimonials-tab">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-3">
        <div>
          <h3 className="text-xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Testimonials</h3>
          <p className="text-xs text-text_muted mt-1">Manage customer testimonials shown on the homepage.</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded font-medium hover:bg-opacity-90"
          data-testid="add-testimonial-btn"
        >
          <Plus size={20} weight="bold" />
          <span>Add Testimonial</span>
        </button>
      </div>

      {loading ? (
        <p className="text-text_muted text-center py-8">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-text_muted text-center py-8">No testimonials yet. Add your first one above.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(t => (
            <div key={t.testimonial_id} className="border border-border p-4 flex gap-4" data-testid={`testimonial-row-${t.testimonial_id}`}>
              <div className="flex-shrink-0">
                {t.image_url ? (
                  <img src={t.image_url} alt={t.customer_name} className="w-16 h-16 rounded-full object-cover border border-border" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-100 border border-border flex items-center justify-center text-lg font-bold text-text_muted">
                    {(t.customer_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold text-primary">{t.customer_name}</div>
                    {t.location && <div className="text-xs text-text_muted">{t.location}</div>}
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${t.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {t.active ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <p className="text-sm text-text_muted mt-2 line-clamp-3">"{t.testimonial}"</p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => openEdit(t)}
                    className="flex items-center space-x-1 text-xs px-3 py-1 border border-border rounded hover:bg-gray-50"
                    data-testid={`edit-testimonial-${t.testimonial_id}`}
                  >
                    <Pencil size={14} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => handleDelete(t)}
                    className="flex items-center space-x-1 text-xs px-3 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
                    data-testid={`delete-testimonial-${t.testimonial_id}`}
                  >
                    <Trash size={14} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="testimonial-form-modal">
          <div className="bg-white border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {editing ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>
              <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded" data-testid="close-testimonial-form-btn" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Customer photo</label>
                <div className="flex items-center gap-3">
                  {form.image_url ? (
                    <img src={form.image_url} alt="preview" className="w-16 h-16 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-border" />
                  )}
                  <label className="cursor-pointer px-3 py-2 border border-border rounded text-sm hover:bg-gray-50" data-testid="upload-testimonial-image-btn">
                    {uploading ? 'Uploading...' : (form.image_url ? 'Replace' : 'Upload')}
                    <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="hidden" />
                  </label>
                  {form.image_url && (
                    <button type="button" onClick={() => setForm(prev => ({ ...prev, image_url: '', image_file_id: null }))} className="text-xs text-red-600 hover:underline" data-testid="clear-testimonial-image-btn">
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Customer name</label>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  required
                  data-testid="testimonial-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <input
                  type="text"
                  placeholder="e.g. Durban, KZN"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  data-testid="testimonial-location-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Testimonial text</label>
                <textarea
                  value={form.testimonial}
                  onChange={(e) => setForm({ ...form, testimonial: e.target.value })}
                  rows="4"
                  className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
                  required
                  data-testid="testimonial-text-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Display order</label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                    className="w-full p-2 border border-border rounded"
                    data-testid="testimonial-order-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="mr-2"
                      data-testid="testimonial-active-input"
                    />
                    <span className="text-sm font-medium">Show on site</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                <button type="button" onClick={closeForm} className="px-6 py-2 border border-border rounded hover:bg-gray-50" data-testid="cancel-testimonial-btn">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-accent text-white rounded font-medium hover:bg-opacity-90 disabled:opacity-50"
                  data-testid="save-testimonial-btn"
                >
                  {saving ? 'Saving...' : (editing ? 'Update' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsTab;
